import { TableProps } from "src/shared/interfaces/charts/Table";
import { sanitizeFilename } from "src/utils/helper/charts/helper";

/**
 * Prefixes cells whose first non-whitespace character is a spreadsheet
 * formula trigger (=, +, -, @) with a single quote so that Excel and
 * similar tools treat the value as plain text rather than a formula.
 * Whitespace-prefixed variants like " =SUM(...)" or "\t=SUM(...)" are also
 * caught because some apps strip leading whitespace before evaluating.
 */
const FORMULA_PREFIX_RE = /^\s*[=+\-@]/;

const escapeCsvCell = (value: string | number | undefined | null): string => {
  const str = String(value ?? "");
  const safe = FORMULA_PREFIX_RE.test(str) ? `'${str}` : str;
  if (safe.includes(",") || safe.includes("\n") || safe.includes("\r") || safe.includes('"')) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

/**
 * Returns true when a string cell value follows the "count (percent)"
 * format used by treatment and response tables, e.g. "42 (35.8%)".
 * The parenthesised part must contain a "%" to avoid false positives on
 * values like "Not reached (median)".
 */
const isCountPercent = (value: string | number): boolean =>
  typeof value === "string" && /^.+\s\(.*%.*\)$/.test(value.trim());

const splitCountPercentValue = (
  value: string | number | undefined | null
): [string, string] => {
  if (value === undefined || value === null) return ["", ""];
  if (typeof value !== "string") return [String(value), ""];
  const parenIndex = value.lastIndexOf(" (");
  if (parenIndex === -1) return [value, ""];
  const count = value.slice(0, parenIndex).trim();
  const percent = value
    .slice(parenIndex + 2)
    .replace(/^\(/, "")
    .replace(/\)$/, "")
    .trim();
  return [count, percent];
};

/**
 * Returns the union of all keys across every row in a table.
 * Using the first row only can miss columns that are absent from it but
 * present in later rows (sparse or irregular datasets).
 */
const getTableKeys = (table: TableProps): string[] =>
  Array.from(
    new Set(table.data.flatMap((item) => Object.keys(item)))
  );

/**
 * Serialises one or more table datasets to a CSV file and triggers a browser
 * download. Each row in the CSV corresponds to one data item; the union of
 * all keys across every row becomes the column headers, optionally remapped
 * via labelMapping.
 *
 * When splitCountPercent is true, any column whose values follow the
 * "N (X%)" format is expanded into two columns: one for the absolute
 * count and one for the percentage (e.g. "Treatment (n)" / "Treatment (%)").
 *
 * Multiple tables are separated by a blank line with a section heading so that
 * the file remains readable when several slides are exported together.
 */
export const downloadTableAsCsv = (
  tables: TableProps[],
  filename: string,
  labelMapping?: Record<string, string>,
  splitCountPercent?: boolean
): void => {
  const rows: string[] = [];

  // Pre-filter so tableIndex and the total count used for section headings
  // and blank-line separators only reflect tables that actually have data.
  // Without this, empty tables at the start of the array cause the first
  // populated table to be incorrectly treated as a later section.
  const nonEmptyTables = tables.filter(
    (table) => table.data && table.data.length > 0
  );

  nonEmptyTables.forEach((table, tableIndex) => {

    // Compute split columns per table: a key is only split here if this
    // table's own rows actually contain count-percent values for it.
    // A global set would force the same split onto unrelated tables that
    // share a key name but store plain values.
    const splitKeys = splitCountPercent
      ? new Set(
          getTableKeys(table).filter((key) =>
            table.data.some(
              (item) => key in item && isCountPercent(item[key])
            )
          )
        )
      : new Set<string>();

    if (tableIndex > 0) {
      rows.push("");
    }

    if (nonEmptyTables.length > 1 && table.name) {
      rows.push(escapeCsvCell(table.name));
    }

    const keys = getTableKeys(table);

    const headers: string[] = [];
    keys.forEach((k) => {
      const label = labelMapping?.[k] ?? k;
      if (splitKeys.has(k)) {
        headers.push(escapeCsvCell(`${label} (n)`));
        headers.push(escapeCsvCell(`${label} (%)`));
      } else {
        headers.push(escapeCsvCell(label));
      }
    });
    rows.push(headers.join(","));

    table.data.forEach((item) => {
      const cells: string[] = [];
      keys.forEach((k) => {
        if (splitKeys.has(k)) {
          const [count, percent] = splitCountPercentValue(item[k]);
          cells.push(escapeCsvCell(count));
          cells.push(escapeCsvCell(percent));
        } else {
          cells.push(escapeCsvCell(item[k]));
        }
      });
      rows.push(cells.join(","));
    });
  });

  if (rows.length === 0) return;

  const csv = rows.join("\n");
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeFilename(filename, "table")}.csv`;
  document.body.appendChild(link);
  link.click();
  // Defer cleanup so the browser has time to consume the blob URL before it
  // is revoked — revoking synchronously after click() can truncate downloads
  // in Safari and some Firefox versions.
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 0);
};
